<template>
    <div class="video-box full-width overflow-hidden">
        <template v-if="cameraError">
            <div class="error-box">{{ cameraError }}</div>
        </template>
        <video playsinline v-else ref="videoRef" autoplay class="video" />
    </div>
    <div class="actions-box flex justify-end items-end full-height no-wrap row">
        <template v-if="videoDevices.length > 0">
            <div class="camera-selector-box flex-basis-50 full-width">
                <div class="full-width">{{ $t("shared.Camera") }}</div>
                <q-select
                    v-model="selectedDevice"
                    class="full-width"
                    :options="videoDevices"
                    @update:model-value="switchCamera"
                    outlined
                    dense
                />
            </div>
        </template>
        <div v-if="!cameraError" class="take-photo-btn-box flex q-mt-lg flex-basis-50 full-width">
            <div class="full-width">
                <QitButton
                    class="full-width"
                    icon="fa-regular fa-camera"
                    :label="$t('shared.Take a photo')"
                    @click="capture"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";
import QitButton from "@/shared/components/buttons/qit-button.vue";
import { resizeElementToBlob } from "@/shared/part-recognition/part-recognition.utils.ts";
import { useUserSettings } from "@/shared/settings/user-settings.ts";

type CameraErrorTypes = "NotAllowedError" | "NotFoundError" | "UnknownError" | "NotReadableError";

interface Option {
    label: string;
    value: string;
}

const CAMERA_KEY = "camera";

const emits = defineEmits<{ onCaptureImage: [blob: Blob] }>();

const video = useTemplateRef<HTMLVideoElement>("videoRef");
const hasCameraPermission = ref<boolean | undefined>(undefined);
const cameraError = ref<CameraErrorTypes | undefined>(undefined);
const selectedDevice = ref<Option>();
const videoDevices = ref<Option[]>([]);

const { data, update } = useUserSettings<string>(CAMERA_KEY, "local");

const stopCurrentStream = () => {
    if (!video.value || !video.value.srcObject) return;

    (video.value.srcObject as MediaStream).getTracks().forEach((track) => track.stop());
    video.value.srcObject = null;
};

onBeforeUnmount(() => {
    stopCurrentStream();
});

onMounted(async () => {
    try {
        // IMPORTANT: getUserMedia must be called first to request camera access permission
        // Only after successful access does enumerateDevices() list the available cameras
        const userMedia = await navigator.mediaDevices.getUserMedia({ video: true });
        userMedia.getTracks().forEach((track) => track.stop());
    } catch {
        return;
    }

    if (video.value) await startCamera();
});

const startCamera = async () => {
    try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (devices.length === 0) return;

        const videInputsDevices = devices.filter((x) => x.kind === "videoinput");
        videoDevices.value = videInputsDevices.map((device) => ({
            label: device.label,
            value: device.deviceId,
        }));

        if (videoDevices.value.length == 0) return;

        selectedDevice.value = data.value
            ? (videoDevices.value.find((x) => x.value === data.value) ?? videoDevices.value[0])
            : videoDevices.value[0];
        await switchCamera(selectedDevice.value);
    } catch (error: unknown) {
        if (error instanceof Error) {
            if (error.name === "NotAllowedError") {
                cameraError.value = "NotAllowedError";
                hasCameraPermission.value = false;
            } else if (error.name === "NotFoundError") {
                cameraError.value = "NotFoundError";
            } else if (error.name === "NotReadableError") {
                cameraError.value = "NotReadableError";
            } else {
                cameraError.value = "UnknownError";
                console.error(error.message);
            }
        } else {
            cameraError.value = "UnknownError";
            console.error(error);
        }

        return;
    }
};

const switchCamera = async (option: { label: string; value: string }) => {
    if (!video.value) return;

    stopCurrentStream();

    video.value.srcObject = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: option.value } },
    });

    await update(option.value);
};

const capture = async () => {
    if (!video.value) return;

    const blob = await resizeElementToBlob(video.value, video.value.videoWidth, video.value.videoHeight);
    if (blob) emits("onCaptureImage", blob);
};
</script>

<style lang="scss" scoped>
video {
    width: 100%;
    height: auto;
    object-fit: contain;
    .screen--xs & {
        height: 100%;
    }
}
.video-box {
    .screen--xs & {
        height: 100%;
    }
}
@media (max-height: 650px) {
    .video-box {
        max-height: 55vh;
    }
    .video {
        max-height: 40vh;
    }
}
.camera-selector-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    .screen--xs & {
        max-width: 40vw;
    }
}
.error-box {
    height: 20vh;
    display: flex;
    align-items: center;
    justify-content: center;
}
.take-photo-btn-box {
    .screen--xs & {
        max-width: 50vw;
    }
}
.actions-box {
    column-gap: $spacing-l;
    .screen--xs & {
        column-gap: $spacing-s;
    }
}
</style>
